import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Award,
  ChevronDown,
  ChevronRight,
  DollarSign,
  FolderTree,
  LayoutDashboard,
  Map,
  MapPin,
  Menu,
  Package,
  PanelLeft,
  PanelLeftClose,
  Percent,
  Receipt,
  Send,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Store,
  Timer,
  Users,
  Wallet
} from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useCan } from "@/admin/components/Can";
import { usePermStore } from "@/admin/auth/usePermStore";
import { ROUTE_PERMS, type RequiredPerms } from "@/admin/auth/permissions";



// Menu item type definition
interface MenuItem {
  title: string;
  icon: any;
  href?: string;
  children?: MenuItem[];
  perm?: string | RequiredPerms;
}

const menuItems: MenuItem[] = [
  {
    title: "Dashboard",
    icon: LayoutDashboard,
    href: "/admin/dashboard",
    perm: ROUTE_PERMS['/admin/dashboard'],
  },
  {
    title: "Stores",
    icon: Store,
    href: "/admin/stores",
    perm: ROUTE_PERMS['/admin/stores'],
  },
  {
    title: "Items & Menu",
    icon: Menu,
    children: [
      {
        title: "All Items",
        icon: Package,
        href: "/admin/items",
        perm: ROUTE_PERMS['/admin/items'],
      },
      {
        title: "Reorder Products",
        icon: Menu,
        href: "/admin/items/reorder",
        perm: ROUTE_PERMS['/admin/items/reorder'],
      },
    ],
  },
  {
    title: "Users",
    icon: Users,
    href: "/admin/users",
    perm: ROUTE_PERMS['/admin/users'],
  },
  {
    title: "Delivery Collection",
    icon: Users,
    children: [
      {
        title: "Delivery Collection",
        icon: Users,
        href: "/admin/drivers",
        perm: ROUTE_PERMS['/admin/drivers'],
      },
      {
        title: "Delivery Collection Log",
        icon: Receipt,
        href: "/admin/collections-log",
        perm: ROUTE_PERMS['/admin/collections-log'],
      },
    ],
  },
  {
    title: "Orders",
    icon: ShoppingCart,
    href: "/admin/orders",
    perm: ROUTE_PERMS['/admin/orders'],
  },
  {
    title: "Live Orders",
    icon: Timer,
    href: "/admin/live-orders",
    perm: ROUTE_PERMS['/admin/live-orders'],
  },
  {
    title: "Eagle View",
    icon: Map,
    href: "/admin/eagle-view",
    perm: ROUTE_PERMS['/admin/eagle-view'],
  },
  {
    title: "Operational Areas",
    icon: MapPin,
    href: "/admin/operational-areas",
    perm: ROUTE_PERMS['/admin/operational-areas'],
  },
  {
    title: 'Categories',
    href: '/admin/categories',
    icon: FolderTree,
    perm: ROUTE_PERMS['/admin/categories'],
  },
  {
    title: 'Mobile Home',
    href: '/admin/mobile',
    icon: Smartphone,
    perm: ROUTE_PERMS['/admin/mobile'],
  },
  {
    title: "App Categories",
    icon: FolderTree,
    href: "/admin/app-categories",
    perm: ROUTE_PERMS['/admin/app-categories'],
  },
  {
    title: "Featured Vendors",
    icon: Award,
    href: "/admin/featured-vendors",
    perm: ROUTE_PERMS['/admin/featured-vendors'],
  },
  {
    title: "Other Stores",
    icon: Store,
    href: "/admin/other-stores",
    perm: ROUTE_PERMS['/admin/other-stores'],
  },
  {
    title: "Featured Products",
    icon: Package,
    href: "/admin/featured-products",
    perm: ROUTE_PERMS['/admin/featured-products'],
  },
  {
    title: "Best Products",
    icon: Award,
    href: "/admin/best-products",
    perm: ROUTE_PERMS['/admin/best-products'],
  },
  {
    title: "Store Payments",
    icon: DollarSign,
    children: [
      {
        title: "Store Balance",
        icon: Wallet,
        href: "/admin/store-balance",
        perm: ROUTE_PERMS['/admin/store-balance'],
      },
      {
        title: "Store Payments",
        icon: DollarSign,
        href: "/admin/store-payouts",
        perm: ROUTE_PERMS['/admin/store-payouts'],
      },
    ],
  },
  {
    title: "Delivery Fee Promotions",
    icon: Percent,
    href: "/admin/delivery-fee-promotions",
    perm: ROUTE_PERMS['/admin/delivery-fee-promotions'],
  },
  {
    title: "Promo Codes",
    icon: Percent,
    href: "/admin/promo-codes",
    perm: ROUTE_PERMS['/admin/promo-codes'],
  },
  {
    title: "Push Notifications",
    icon: Send,
    href: "/admin/push-notifications",
    perm: ROUTE_PERMS['/admin/push-notifications'],
  },
  {
    title: "Wallet Transactions",
    icon: Wallet,
    href: "/admin/wallet-transactions",
    perm: ROUTE_PERMS['/admin/wallet-transactions'],
  },
  {
    title: "Administration",
    icon: ShieldCheck,
    children: [
      {
        title: "Staff",
        icon: Users,
        href: "/admin/staff",
        perm: ROUTE_PERMS['/admin/staff'],
      },
      {
        title: "Roles",
        icon: Award,
        href: "/admin/roles",
        perm: ROUTE_PERMS['/admin/roles'],
      },
      {
        title: "Audit Logs",
        icon: Receipt,
        href: "/admin/audit-logs",
        perm: ROUTE_PERMS['/admin/audit-logs'],
      },
      // {
      //   title: "Payouts",
      //   icon: DollarSign,
      //   href: "/admin/payouts",
      //   perm: ROUTE_PERMS['/admin/payouts'],
      // },
    ],
  },
];

function hrefMatches(href: string, pathname: string): boolean {
  if (pathname === href) return true;
  if (href.startsWith("/admin/")) {
    if (pathname === href.slice("/admin".length)) return true;
    if (href === "/admin/dashboard" && pathname === "/") return true;
    if (pathname.startsWith(href + "/")) return true;
  } else if (href !== "/") {
    if (pathname === `/admin${href}`) return true;
    if (pathname.startsWith(href + "/")) return true;
  }
  return false;
}

export default function Sidebar() {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const can = useCan();
  const permsLoaded = usePermStore((s) => s.fetchedAt !== null);
  const visibleItems = menuItems
    .map((item) =>
      item.children
        ? { ...item, children: item.children.filter((c) => !c.perm || !permsLoaded || can(c.perm)) }
        : item,
    )
    .filter((item) => {
      if (item.children) {
        if (item.children.length > 0) return true;
        return !!item.href && (!item.perm || !permsLoaded || can(item.perm));
      }
      return !item.perm || !permsLoaded || can(item.perm);
    });

  const toggleExpanded = (title: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(title)) {
      newExpanded.delete(title);
    } else {
      newExpanded.add(title);
    }
    setExpandedItems(newExpanded);
  };

  const isItemActive = (item: MenuItem): boolean => {
    if (item.href) {
      return hrefMatches(item.href, location.pathname);
    }
    if (item.children) {
      return item.children.some((child) => child.href && hrefMatches(child.href, location.pathname));
    }
    return false;
  };

  return (
    <nav
      aria-label="Admin navigation"
      className={cn(
        "relative border-r bg-background transition-all duration-300 overflow-hidden",
        isCollapsed ? "w-16" : "w-64"
      )}
    >
      <Button
        variant="ghost"
        size="icon"
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-expanded={!isCollapsed}
        className="absolute -right-3 top-4 h-8 w-8 rounded-full border bg-background z-10"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        {isCollapsed ? (
          <PanelLeft className="h-4 w-4" />
        ) : (
          <PanelLeftClose className="h-4 w-4" />
        )}
      </Button>

      <div className="pb-12 min-h-screen overflow-y-auto">
        <div className="space-y-4 py-4">
          <div className="px-3 py-2">
            <div className="space-y-1" role="list">
              {visibleItems.map((item) => {
                const isActive = isItemActive(item);
                const hasChildren = item.children && item.children.length > 0;

                if (hasChildren) {
                  return (
                    <div key={item.title} role="listitem">
                      <button
                        onClick={() => toggleExpanded(item.title)}
                        aria-expanded={expandedItems.has(item.title)}
                        className={cn(
                          "flex items-center justify-between w-full rounded-lg px-2 py-2 text-sm transition-all hover:text-primary",
                          isActive
                            ? "bg-muted font-medium text-primary"
                            : "text-muted-foreground",
                          isCollapsed ? "justify-center" : "gap-3"
                        )}
                        title={isCollapsed ? item.title : undefined}
                      >
                        <div className="flex items-center gap-3">
                          <item.icon className={cn(
                            "transition-all",
                            isCollapsed ? "h-5 w-5" : "h-4 w-4"
                          )} />
                          {!isCollapsed && item.title}
                        </div>
                        {!isCollapsed && (
                          <div className="transition-transform">
                            {expandedItems.has(item.title) ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </div>
                        )}
                      </button>
                      {!isCollapsed && expandedItems.has(item.title) && (
                        <div className="ml-4 mt-1 space-y-1" role="list">
                          {item.children?.map((child) => {
                            const childActive = child.href && hrefMatches(child.href, location.pathname);
                            return (
                              <Link
                                key={child.href}
                                to={child.href!}
                                aria-current={childActive ? "page" : undefined}
                                className={cn(
                                  "flex items-center rounded-lg px-2 py-2 text-sm transition-all hover:text-primary",
                                  childActive
                                    ? "bg-muted font-medium text-primary"
                                    : "text-muted-foreground",
                                  "gap-3"
                                )}
                              >
                                <child.icon className="h-4 w-4" />
                                {child.title}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div key={item.href} role="listitem">
                    <Link
                      to={item.href!}
                      aria-current={isActive ? "page" : undefined}
                      className={cn(
                        "flex items-center rounded-lg px-2 py-2 text-sm transition-all hover:text-primary",
                        isActive
                          ? "bg-muted font-medium text-primary"
                          : "text-muted-foreground",
                        isCollapsed ? "justify-center" : "gap-3"
                      )}
                      title={isCollapsed ? item.title : undefined}
                    >
                      <item.icon className={cn(
                        "transition-all",
                        isCollapsed ? "h-5 w-5" : "h-4 w-4"
                      )} />
                      {!isCollapsed && item.title}
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
